import { differenceInDays, isBefore } from 'date-fns';
import {
  ITEM_HEIGHT,
  ITEM_HEIGHT_COMPACT,
  ITEM_LANE_HEIGHT,
  ITEM_LANE_HEIGHT_COMPACT,
  MIN_ITEM_WIDTH,
} from '../TimelineItemConstants';

export const clampStartDateToWindow = (
  normalizedStart: string | undefined,
  timelineStartDate: string,
): string => {
  if (normalizedStart && isBefore(normalizedStart, timelineStartDate)) {
    return timelineStartDate;
  }
  return normalizedStart ?? timelineStartDate;
};

export const clampEndDateToWindow = (
  normalizedFinish: string | undefined,
  timelineEndDate: string,
): string => {
  if (normalizedFinish && isBefore(normalizedFinish, timelineEndDate)) {
    return normalizedFinish ?? timelineEndDate;
  }
  return timelineEndDate;
};

export const calculateItemWidth = (
  startDateToUse: string,
  endDateToUse: string,
  dayWidth: number,
): number => {
  const numDaysInItem = differenceInDays(endDateToUse, startDateToUse) + 1;
  return Math.max(dayWidth * numDaysInItem, MIN_ITEM_WIDTH);
};

export const getSingleLaneHeight = (isCompact: boolean): number =>
  isCompact ? ITEM_LANE_HEIGHT_COMPACT : ITEM_LANE_HEIGHT;

export const getItemHeight = (isCompact: boolean): number =>
  isCompact ? ITEM_HEIGHT_COMPACT : ITEM_HEIGHT;

export const calculateMaxContainerHeight = (
  singleLaneHeight: number,
  maxChildOrder: number,
  hasChildren: boolean,
): number => (hasChildren ? (1 + maxChildOrder + 1) * singleLaneHeight : singleLaneHeight);

const EXPANDED_BACKGROUND_PADDING = 10;
const EXPANDED_BACKGROUND_PADDING_COMPACT = 4;
// White gap kept above and below an expanded group's background so it floats
// within its row instead of sitting flush against the neighboring rows.
const EXPANDED_BACKGROUND_MARGIN = 4;
const EXPANDED_BACKGROUND_MARGIN_COMPACT = 4;
// Extra slack between the top border and the first child so the border reads
// as a frame around the children rather than hugging the top capsule.
const EXPANDED_BACKGROUND_TOP_INSET = 6;
const EXPANDED_BACKGROUND_TOP_INSET_COMPACT = 4;

export const getExpandedChildrenBackgroundStyle = ({
  isCompact,
  itemTopOffset,
  itemHeight,
  singleLaneHeight,
  maxChildOrder,
  bgLeft,
  bgRight,
  timelineStartRelative,
  timelineEndRelative,
}: {
  isCompact: boolean;
  itemTopOffset: number;
  itemHeight: number;
  singleLaneHeight: number;
  maxChildOrder: number;
  bgLeft: number;
  bgRight: number;
  timelineStartRelative: number;
  timelineEndRelative: number;
}): { top: number; height: number; left: number; right: number } => {
  const bgPadding = isCompact ? EXPANDED_BACKGROUND_PADDING_COMPACT : EXPANDED_BACKGROUND_PADDING;
  const outerMargin = isCompact ? EXPANDED_BACKGROUND_MARGIN_COMPACT : EXPANDED_BACKGROUND_MARGIN;
  const topInset = isCompact
    ? EXPANDED_BACKGROUND_TOP_INSET_COMPACT
    : EXPANDED_BACKGROUND_TOP_INSET;
  // The vertical slack between the items and the lane edges equals itemTopOffset
  // on each side, so the padding must never exceed it or the background bleeds
  // into neighboring rows.
  const verticalBgPadding = Math.min(bgPadding, itemTopOffset);
  const childrenBottom =
    singleLaneHeight + maxChildOrder * singleLaneHeight + itemTopOffset + itemHeight;
  // Start below the parent's capsule so the (potentially wider) background never
  // overlaps top-level items sitting next to the parent on its own row. The
  // topInset lifts the top border off the first child so it frames it.
  const top = singleLaneHeight + itemTopOffset - verticalBgPadding + outerMargin - topInset;
  const bottom = childrenBottom + verticalBgPadding - outerMargin;
  const height = Math.max(bottom - top, 0);
  const left = Math.max(bgLeft - bgPadding, timelineStartRelative);
  const right = Math.max(bgRight - bgPadding, timelineEndRelative);
  return { top, height, left, right };
};

export const isOutsideTimelineWindow = (
  hasAnyPlannedDate: boolean,
  startDateToUse: string,
  endDateToUse: string,
  timelineStartDate: string,
  timelineEndDate: string,
): boolean => {
  if (!hasAnyPlannedDate) return true;
  if (isBefore(new Date(timelineEndDate), new Date(startDateToUse))) return true;
  if (isBefore(new Date(endDateToUse), new Date(timelineStartDate))) return true;
  return false;
};
